import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Grid from '@mui/material/Grid';
import { colors, simpleFetch, yearList } from '../../../utils/helpsers'
import VariableFilter from "../../molecules/VariableFilter/VariableFilter";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MapView from "../MapView/MapView";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Checkbox from '@mui/material/Checkbox';
import { MultiVariableFilterStyles } from "./MultiVariableFilter.styles";

/**
 * Execute a fetch request filtering the districts using the values in the filters and display a map.
 * @param { boolean } withCustomYear - True/false value to display a dropdown with years available.
 * @param { function } onApply - A function to be execute on click the apply button.
 * @param { object } initData - Data to be displayed on load.
 * @param { object } year - The year to fetch the variable data.
 * @param { object } referenceDistrict - The reference district label and the irn value.
 * @param { boolean } disabled - Value to control the disabled state.
 * @param { boolean } withMap - Value to display the map.
 * @param { string, object } irns - list with district group irn.
 * @param { number } limit - the maximum number of filter to display 1 to 6 are allowed values.
 * @param { boolean } returnMapData - True if the apply function should return the data of the map.
 * @param { boolean } withHealthVariables - True/false to exclude Health Benefits variables from select component.
 * @param { boolean } withSalaryVars - True/false to exclude Salary Benefits variables from select component.
 * @returns {JSX.Element}
 */

function MultiVariableFilter({ withMap = true, withCustomYear, onApply, initData = { var1: {} }, year, referenceDistrict, disabled, irns, limit = 6, returnMapData = false, withHealthVariables = true, withSalaryVars = true }) {
  const [multiFilterData, setMultiFilerData] = useState(initData);
  // Actions: addFilter and Apply.
  const [disabledActions, setDisabledActions] = useState(true);
  const [referenceYear, setReferenceYear] = useState(year || Object.keys(yearList).pop());
  const [variableOptions, setVariableOptions] = useState();
  const [mapLastFilter, setMapLastFilter] = useState();
  const [customYearInput, setCustomYearInput] = useState(false);
  const [mapData, setMapData] = useState();
  const [mapAllFilters, setMapAllFilters] = useState();
  const [loadingData, setLoadingData] = useState();
  const [highlightLastFilter, setHighlightLastFilter] = useState(false);

  const multiFilterDataRef = useRef({
    vars: {
      var1: { values: null },
    }
  });

  const sourceVariables = useRef();

  useEffect(() => {
    if (irns && year.value) {
      //Getting data Map for group
      if (year.value === 'MCD') {
        simpleFetch(
            `${process.env.REACT_APP_API_URL}/hvd_polygons_mcd?irn=in.(${irns})`
        ).then((polygons) => {
          setMapData(
              polygons.map((item) => {
                return {
                  ...item
                };
              })
          );
        });
      } else {
        simpleFetch(
            `${process.env.REACT_APP_API_URL}/hvd_polygons?irn=in.(${irns})&file_year=eq.${year.value}`
        ).then((polygons) => {
          setMapData(
              polygons.map((item) => {
                return {
                  ...item
                };
              })
          );
        });
      }
    }
  }, [irns, year.value]);


  // On year or district update, reset the filters.
  useEffect(() => {
    if (year.value || referenceDistrict.value) {
      setReferenceYear(year);
      setMultiFilerData({ var1: {} });
      multiFilterDataRef.current.vars = { var1: {} };
      multiFilterDataRef.current.referenceDistrict = referenceDistrict;
      setVariableOptions();
      multiFilterDataRef.current.year = year;
    }
  }, [year, referenceDistrict])

  let currentDistrict = useRef({});

  useEffect(() => {
    if(referenceDistrict) {
      currentDistrict.current = referenceDistrict
    }
  }, [referenceDistrict]);

  useEffect(() => {
    if (referenceYear?.value === year?.value) {
      if (referenceYear.value === 'MCD') {
        simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/mcd_sv_year?_irn=${referenceDistrict.value}&dwh_table=neq.health_insurance_readable&visible=eq.true&order=label.asc`)
          .then(data => {
            if(!withSalaryVars) {
              data = data.filter( el => el.domain_name !== "Teacher Salary")
            }
            setVariableOptions(data.map( el => {
              const mcdYear = el?.available_years?.[0];
              if(!el.label?.includes("20")) return { ...el, label: `${el.label} (${mcdYear})`}
              return el;
            }));
            sourceVariables.current = data;
          })
          .then(() => {
            if (withHealthVariables) {
              simpleFetch(
                `${process.env.REACT_APP_API_URL}/mcd_sv_health?erirn_employer_ein=eq.${currentDistrict.current.value}&visible=eq.true&order=label.asc`
              )
                .then((data) => {
                  setVariableOptions(previousData => {
                    let concatedData = previousData.concat(data);
                    sourceVariables.current = concatedData;
                    return concatedData
                  });
                })
            }
          })
      } else {
        simpleFetch(`${process.env.REACT_APP_API_URL}/hvd_source_variable?file_year=eq.${referenceYear.value}&dwh_table=neq.health_insurance_readable&visible=eq.true&order=label.asc`)
          .then(data => {
            if(!withSalaryVars) {
              data = data.filter( el => el.domain_name !== "Teacher Salary")
            }
            setVariableOptions(data);
            sourceVariables.current = data;
          })
          .then(() => {
            if (withHealthVariables) {
              simpleFetch(
                `${process.env.REACT_APP_API_URL}/hvd_sv_health?file_year=eq.${referenceYear.value}&erirn_employer_ein=eq.${currentDistrict.current.value}&visible=eq.true&order=label.asc`
              )
                .then((data) => {
                  setVariableOptions(previousData => {
                    let concatedData = previousData.concat(data);
                    sourceVariables.current = concatedData;
                    return concatedData
                  });
                })
            }
          }
          )
      }
    }
  }, [referenceDistrict, referenceYear, year, withHealthVariables, withSalaryVars])

  // If init data is updated load it.
  useEffect(() => {
    const lastKey = Object.keys(initData).pop();
    if (irns && initData[lastKey].hasOwnProperty('districtsCount')) {
      setMultiFilerData(initData);
      multiFilterDataRef.current.vars = initData;
      applyFilters.current(multiFilterDataRef, irns)
    }
  }, [initData, irns]);

  // highlight map last filter fn.
  useEffect(() => {
    highlightLastFilter ? setMapData(mapLastFilter) : setMapData(mapAllFilters);
  }, [highlightLastFilter, mapAllFilters, mapLastFilter])

  const updateFilterGroupData = useCallback((id, newValue) => {
    if (multiFilterDataRef.current.vars[id]?.value_var !== newValue.value_var) {
      multiFilterDataRef.current.vars[id] = newValue;
      let validation = Object.values(multiFilterDataRef.current.vars).every(x => x.hasOwnProperty('label'));
      setDisabledActions(!validation);

      let clonedOptions = structuredClone(sourceVariables.current);
      for (const value of Object.values(multiFilterDataRef.current.vars)) {
        for (const [optKey, optValue] of Object.entries(sourceVariables.current)) {
          if (value.value === optValue.value) {
            clonedOptions[optKey] = { ...clonedOptions[optKey], disabled: true }
            break
          }
        }
      }
      setVariableOptions(clonedOptions)
    }

  }, [])

  const applyFilters = useRef((filtersData, irns) => {
    let var1 = filtersData.current.vars.var1;
    let var2 = filtersData.current.vars.var2;
    let var3 = filtersData.current.vars.var3;
    let var4 = filtersData.current.vars.var4;
    let var5 = filtersData.current.vars.var5;
    let var6 = filtersData.current.vars.var6;

    /** TODO Remove if validation and varName key once the saved groups structure is updated */
    const lastKey = Object.keys(filtersData.current.vars).pop();
    let query;
    if (filtersData.current.vars[lastKey].hasOwnProperty('varName')) {
      let range = var1.range ? 'range' : 'values';
      query = `${filtersData.current.referenceDistrict.value}&_var1=${var1 ? var1.varName : 'NA'}&_min_var1=${var1 ? var1[range][0] : 'NA'}&_max_var1=${var1 ? var1[range][1] : 'NA'}&_var2=${var2 ? var2.varName : 'NA'}&_min_var2=${var2 ? var2[range][0] : 'NA'}&_max_var2=${var2 ? var2[range][1] : 'NA'}&_var3=${var3 ? var3.varName : 'NA'}&_min_var3=${var3 ? var3[range][0] : 'NA'}&_max_var3=${var3 ? var3[range][1] : 'NA'}&_var4=${var4 ? var4.varName : 'NA'}&_min_var4=${var4 ? var4[range][0] : 'NA'}&_max_var4=${var4 ? var4[range][1] : 'NA'}&_var5=${var5 ? var5.varName : 'NA'}&_min_var5=${var5 ? var5[range][0] : 'NA'}&_max_var5=${var5 ? var5[range][1] : 'NA'}&_var6=${var6 ? var6.varName : 'NA'}&_min_var6=${var6 ? var6[range][0] : 'NA'}&_max_var6=${var6 ? var6[range][1] : 'NA'}&_year=${filtersData.current.year.value}`;

    } else {
      query = `${filtersData.current.referenceDistrict.value}&_var1=${var1 ? var1.value_var : 'NA'}&_min_var1=${var1 ? var1.values[0] : 'NA'}&_max_var1=${var1 ? var1.values[1] : 'NA'}&_var2=${var2 ? var2.value_var : 'NA'}&_min_var2=${var2 ? var2.values[0] : 'NA'}&_max_var2=${var2 ? var2.values[1] : 'NA'}&_var3=${var3 ? var3.value_var : 'NA'}&_min_var3=${var3 ? var3.values[0] : 'NA'}&_max_var3=${var3 ? var3.values[1] : 'NA'}&_var4=${var4 ? var4.value_var : 'NA'}&_min_var4=${var4 ? var4.values[0] : 'NA'}&_max_var4=${var4 ? var4.values[1] : 'NA'}&_var5=${var5 ? var5.value_var : 'NA'}&_min_var5=${var5 ? var5.values[0] : 'NA'}&_max_var5=${var5 ? var5.values[1] : 'NA'}&_var6=${var6 ? var6.value_var : 'NA'}&_min_var6=${var6 ? var6.values[0] : 'NA'}&_max_var6=${var6 ? var6.values[1] : 'NA'}&_year=${filtersData.current.year.value}`;
    }

    simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_map_filter_health_cg?_reference=${query}&_irns=${irns}`)
      .then(data => {
        let filtersLength = Object.keys(filtersData.current.vars).length;
        let mapPolygons = data.map(district => {
          return { ...district, 'color': colors[`${district.filter - 1}`] }
        })

        // Store district by filters result.
        for (let i = 1; i <= filtersLength; i++) {
          let group = mapPolygons.filter(district => district.filter === i);

          if (i > 1 || i < filtersLength) {
            for (let d = i; d < filtersLength; d++) {
              let nextFilterGroup = mapPolygons.filter(district => district.filter === d + 1);
              group = [...group, ...nextFilterGroup];
            }
          }

          filtersData.current.vars[`var${i}`].districtsCount = group.length;
          filtersData.current.vars[`var${i}`].irns = group.map(district => district.irn);

          // Assign las filter in a hook to set "show last filter" option.
          if (i === filtersLength) {
            setMapLastFilter(group);
            filtersData.current.group_length = group.length;
            filtersData.current.vars[`var${i}`].districtsCount = group.length;
          }

          // Update filter count (except first and last).
          filtersData.current.vars[`var${i}`].districtsCount = group.length;
        }

        setMultiFilerData(filtersData.current.vars);

        setMapData(mapPolygons);
        setMapAllFilters(mapPolygons);
        setLoadingData(false);

        returnMapData ? onApply({filtersData: multiFilterDataRef, mapData: mapPolygons}) : onApply(multiFilterDataRef);
      })
  })

  return (
    <Grid container spacing={1} className="multiVariableFilters" sx={MultiVariableFilterStyles}>
      {
        withMap && (
          <Grid item xs={12} md={4} className="multiVariableFilters__map">
            {
              loadingData &&
              <div className={"custom-groups__loading"}>
                <div className={"custom-groups__loading-message"}>Loading data...</div>
              </div>
            }
            {(mapData && referenceYear) && (
              <>
                <MapView
                  data={mapData}
                  districtIrn={
                    referenceDistrict ? referenceDistrict.value : null
                  }
                  height="390px"
                  zoom={6.5}
                />
                {(multiFilterData?.var2 && mapLastFilter) && (
                  <FormGroup className="highlight-switch">
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={highlightLastFilter}
                          onChange={() => setHighlightLastFilter(!highlightLastFilter)}
                          disabled={
                            !(
                              multiFilterData[`var${Object.keys(multiFilterData).length}`] &&
                              multiFilterData[`var${Object.keys(multiFilterData).length}`]
                                .districtsCount
                            )
                          }
                        />
                      }
                      label="Show last filter only"
                    />
                  </FormGroup>
                )}
              </>
            )}
          </Grid>
        )
      }
      <Grid item xs={12} md={withMap ? 8 : 12} className="multiVariableFilters__filters">
        {
          withCustomYear &&
          <div className="multiVariableFilters__custom-year">
            <Autocomplete
              id="year"
              value={referenceYear}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={yearList}
              disabled={!customYearInput}
              onChange={(event, newValue) => {
                if (newValue) {
                  // Cleaning hook
                  setVariableOptions();
                  setMultiFilerData({ var1: {} });
                  multiFilterDataRef.current.vars = { var1: {} };
                  // Updating hooks
                  setReferenceYear(newValue);
                  multiFilterDataRef.current.year = newValue;
                  window._paq.push([
                    "trackEvent",
                    "Multi Variable Filter Custom Year",
                    "Clicked: Year",
                    `Selected: ${newValue.label}`,
                  ]);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={"Year"} required={true} />
              )}
            />
            <FormGroup>
              <FormControlLabel control={<Checkbox onChange={() => setCustomYearInput(!customYearInput)} />} label="Enable custom year" />
            </FormGroup>
          </div>
        }
        <div>
          <Grid container spacing={1} className="multiVariableFilters__filters-group">
            {(multiFilterData && variableOptions) &&
              Object.keys(multiFilterData).map((filter, key) => {
                const id = key + 1;
                return (
                  <Grid item xs={12} lg={6} key={key}>
                    <div className={`var-option var-${id}`}>
                      <span
                        className="var-option__color-indicator"
                        style={{ backgroundColor: colors[key] }}
                      ></span>
                      {Object.keys(multiFilterData).length === id && id !== 1 && !disabled && (
                        <Button
                          className="remove-filter"
                          variant="contained"
                          color="secondary"
                          onClick={() => {
                            // Removing last filter value
                            setMultiFilerData((prevState) => {
                              let newState = { ...prevState };
                              delete newState[`var${id}`];
                              return newState;
                            });

                            // If a variable as been set, on delete filter enable the variable.
                            if (multiFilterDataRef.current.vars[`var${id}`]) {
                              let clonedOptions = structuredClone(variableOptions);
                              for (const [key, value] of Object.entries(clonedOptions)) {
                                if (value.value === multiFilterDataRef.current.vars[`var${id}`].value) {
                                  delete clonedOptions[key].disabled
                                  break;
                                }
                              }
                              setVariableOptions(clonedOptions)
                            }

                            // Removing las current custom range.
                            delete multiFilterDataRef.current.vars[`var${id}`];

                            let validation = Object.values(multiFilterDataRef.current.vars).every(x => x.hasOwnProperty('label'));
                            setDisabledActions(!validation);
                          }}
                        >
                          x
                        </Button>
                      )}
                      <span className="filter__counter">
                        {multiFilterData[`var${id}`] &&
                          multiFilterData[`var${id}`].districtsCount &&
                          `${multiFilterData[`var${id}`].districtsCount
                          } districts left`}
                      </span>
                      <VariableFilter
                        defaultInfo={multiFilterData[`var${id}`]}
                        id={id}
                        variables={variableOptions}
                        referenceDistrict={referenceDistrict}
                        year={referenceYear}
                        disabled={disabled}
                        onChange={updateFilterGroupData}
                      />
                    </div>
                  </Grid>
                );
              })}
          </Grid>
          <div className="custom-groups__filter-actions">
            {Object.keys(multiFilterData).length < limit && (
              <Button
                color="primary"
                disabled={disabledActions}
                onClick={() => {
                  window._paq.push([
                    "trackEvent",
                    "Board CustomGroups",
                    "Clicked: Add filters",
                  ]);
                  let activeFilters = Object.keys(multiFilterData).length;
                  if (activeFilters < limit) {
                    setMultiFilerData((prevState) => ({
                      ...prevState,
                      [`var${activeFilters + 1}`]: {},
                    }));
                  }
                  setDisabledActions(true);
                }}
              >
                + Add filter
              </Button>
            )}
            {(multiFilterData?.var1 && !disabled) && (
              <Button
                disabled={disabledActions}
                id={'apply_filters'}
                className={"submit-filters"}
                variant="contained"
                style={{
                  backgroundColor: disabledActions ? "#CCC" : "#773DBD",
                  color: "#fff",
                }}
                onClick={() => {
                  setLoadingData(true)
                  window._paq.push([
                    "trackEvent",
                    "Board CustomGroups",
                    "Clicked: Apply filters",
                  ]);
                  window._paq.push(["trackGoal", 8]);
                  applyFilters.current(multiFilterDataRef, irns);
                }}
              >
                Apply filters
              </Button>
            )}
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

MultiVariableFilter.propTypes = {
  withMap: PropTypes.bool,
  withCustomYear: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  initData: PropTypes.object,
  year: PropTypes.object,
  referenceDistrict: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  returnMapData: PropTypes.bool,
  withHealthVariables: PropTypes.bool,
  withSalaryVars: PropTypes.bool,
  irns: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.string.isRequired,
  ]),
}

export default React.memo(MultiVariableFilter);
